/**
 * Shooter Portal — for users with `role=photographer` or `role=videographer`.
 * Upload photos/videos, set price + watermark text, edit/delete listings.
 *
 * Photos: single base64 upload (≤15MB, one shot to /api/marketplace/items).
 * Videos: pipes through the existing Rodeo Box multipart upload, then
 * calls `/api/marketplace/items` with the resulting video_id.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { api, BACKEND_URL, fmtDuration } from "@/lib/api";
import { useAuth } from "@/auth/AuthContext";
import { PortalShell } from "@/pages/ClientPortalShell";
import { toast } from "sonner";
import {
  Upload, Camera, Video, Trash2, Pencil, DollarSign, Save,
  Eye, EyeOff, ImageIcon, Loader2, Film, Search, X,
} from "lucide-react";
import { FolderSidebar, MoveToFolderMenu, useFolders, filterByFolder } from "@/components/Folders";
import { uploadPhotoFast, uploadVideoFast } from "@/lib/fastUpload";
import { errMsg } from "@/lib/errMsg";

const dollars = (cents) => `$${(cents / 100).toFixed(2)}`;

export default function ShooterPortal() {
  const { user, refresh } = useAuth();
  const isPhoto = user?.role === "photographer";
  const kind = isPhoto ? "photo" : "video";
  const scope = isPhoto ? "shooter_photos" : "shooter_videos";
  const [items, setItems] = useState([]);
  const [wmText, setWmText] = useState(user?.watermark_text || "");
  const [savingWm, setSavingWm] = useState(false);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);
  const [priceCents, setPriceCents] = useState(1000);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [activeFolder, setActiveFolder] = useState("all");
  const [search, setSearch] = useState("");
  const fileRef = useRef(null);

  const folderState = useFolders(scope);

  const load = () => api.get("/marketplace/me/items").then((r) => setItems(r.data.items || []));

  useEffect(() => {
    load();
  }, []);

  const saveWatermark = async () => {
    setSavingWm(true);
    try {
      await api.patch("/auth/me", { watermark_text: wmText });
      toast.success("Watermark saved");
      refresh && refresh();
    } catch (e) {
      toast.error("Save failed: " + (e?.response?.data?.detail || e.message));
    } finally {
      setSavingWm(false);
    }
  };

  const uploadPhoto = async (file) => {
    if (!title.trim()) { toast.error("Set a title first"); return; }
    setBusy(true); setProgress(5);
    try {
      const { item_id } = await uploadPhotoFast({
        file,
        onProgress: (p) => setProgress(Math.max(5, Math.min(95, p))),
      });
      setProgress(97);
      // Server has the S3 object; finalize creates the marketplace row
      await api.post("/marketplace/items/photo-finalize", {
        item_id,
        title: title.trim(),
        description: description.trim(),
        price_cents: Number(priceCents) || 0,
        size_bytes: file.size,
      });
      setProgress(100);
      toast.success("Photo listed");
      setTitle(""); setDescription("");
      await load();
    } catch (e) {
      toast.error("Upload failed: " + errMsg(e));
    } finally {
      setBusy(false); setProgress(0);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const uploadVideo = async (file) => {
    if (!title.trim()) { toast.error("Set a title first"); return; }
    setBusy(true); setProgress(0);
    try {
      const { video_id } = await uploadVideoFast({
        file,
        onProgress: (p) => setProgress(Math.max(1, Math.min(95, p))),
      });
      setProgress(97);
      await api.post("/marketplace/items", {
        kind: "video",
        title: title.trim(),
        description: description.trim(),
        price_cents: Number(priceCents) || 0,
        video_id,
      });
      setProgress(100);
      toast.success("Video listed");
      setTitle(""); setDescription("");
      await load();
    } catch (e) {
      toast.error("Upload failed: " + errMsg(e));
    } finally {
      setBusy(false); setProgress(0);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (isPhoto) uploadPhoto(f);
    else uploadVideo(f);
  };

  const renameItem = async (it) => {
    const next = window.prompt("Rename title", it.title || "");
    if (next == null || next.trim() === (it.title || "").trim()) return;
    try {
      await api.patch(`/marketplace/items/${it.id}`, { title: next.trim() });
      toast.success("Renamed");
      load();
    } catch (e) {
      toast.error("Rename failed");
    }
  };

  const repriceItem = async (it) => {
    const dollars = window.prompt("New price (e.g. 15.00)", ((it.price_cents || 0) / 100).toFixed(2));
    if (dollars == null) return;
    const cents = Math.round(parseFloat(dollars) * 100);
    if (!Number.isFinite(cents) || cents < 0) { toast.error("Invalid price"); return; }
    try {
      await api.patch(`/marketplace/items/${it.id}`, { price_cents: cents });
      toast.success("Price updated");
      load();
    } catch (e) {
      toast.error("Save failed");
    }
  };

  const toggleActive = async (it) => {
    try {
      await api.patch(`/marketplace/items/${it.id}`, { active: !it.active });
      load();
    } catch { toast.error("Save failed"); }
  };

  const deleteItem = async (it) => {
    if (!window.confirm(`Delete "${it.title}"? Buyers who already own it keep their download.`)) return;
    try {
      await api.delete(`/marketplace/items/${it.id}`);
      toast.success("Deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  // Only show items whose kind matches this shooter's role, then apply folder + search
  const visibleItems = useMemo(() => {
    const kindMatched = items.filter((it) => it.kind === kind);
    const byFolder = filterByFolder(kindMatched, activeFolder);
    const q = search.trim().toLowerCase();
    if (!q) return byFolder;
    return byFolder.filter(
      (it) => (it.title || "").toLowerCase().includes(q) ||
              (it.description || "").toLowerCase().includes(q),
    );
  }, [items, kind, activeFolder, search]);

  const reloadItemsAndFolders = async () => { await load(); folderState.reload(); };

  return (
    <PortalShell>
      <div className="border-l-4 border-sky-600 pl-5 mb-8">
        <div className="label-tiny mb-2">{isPhoto ? "Photographer Studio" : "Videographer Studio"}</div>
        <h1 className="font-display uppercase text-5xl font-bold leading-[0.95]">Your Gallery</h1>
        <p className="text-stone-400 mt-3">
          Upload {isPhoto ? "photos" : "videos"} for sale on the Rodeo Cut Studio marketplace.
          Preview images/videos carry your overlay text; buyers who purchase get a clean download.
        </p>
      </div>

      {/* Watermark text */}
      <div className="border border-stone-800 bg-stone-950 p-4 mb-8" data-testid="shooter-watermark">
        <div className="flex items-center gap-2 mb-2">
          <ImageIcon size={13} className="text-sky-400" />
          <div className="font-display uppercase text-sm tracking-wider">Your Watermark Text</div>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="text"
            value={wmText}
            onChange={(e) => setWmText(e.target.value)}
            placeholder="e.g. © J. Smith Rodeo Photography"
            maxLength={120}
            className="field-input flex-1"
            data-testid="shooter-watermark-input"
          />
          <button
            onClick={saveWatermark}
            disabled={savingWm}
            className="btn-primary inline-flex items-center gap-1.5 text-sm"
            data-testid="shooter-watermark-save"
          >
            {savingWm ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
            Save
          </button>
        </div>
        <div className="text-[11px] text-stone-500 mt-2">
          This text is overlaid on every preview image/video in the marketplace.
          After a buyer purchases, they download the clean original.
        </div>
      </div>

      {/* Upload form */}
      <div className="border border-stone-800 p-5 mb-8" data-testid="shooter-upload">
        <div className="flex items-center gap-2 mb-3">
          {isPhoto ? <Camera size={14} className="text-sky-400" /> : <Video size={14} className="text-sky-400" />}
          <div className="font-display uppercase text-lg">List a new {isPhoto ? "photo" : "video"}</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
          <input
            type="text"
            placeholder="Title (e.g. Barrel Rider #14 · Round 2)"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="field-input md:col-span-2"
            data-testid="shooter-upload-title"
          />
          <textarea
            placeholder="Short description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="field-input md:col-span-2 min-h-[56px]"
            data-testid="shooter-upload-description"
          />
          <label className="text-[11px] uppercase tracking-widest text-stone-500 md:col-span-2 -mb-2">
            Price
          </label>
          <div className="relative">
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-stone-500 text-sm">$</span>
            <input
              type="number"
              min={0}
              step={0.5}
              value={(priceCents / 100).toFixed(2)}
              onChange={(e) => setPriceCents(Math.round((parseFloat(e.target.value) || 0) * 100))}
              className="field-input pl-6"
              data-testid="shooter-upload-price"
            />
          </div>
          <div className="text-xs text-stone-500 self-center">
            All prices in CAD. Platform takes payment; payouts handled by admin.
          </div>
        </div>
        <input
          ref={fileRef}
          type="file"
          accept={isPhoto ? "image/*" : "video/*"}
          onChange={onFile}
          disabled={busy}
          className="hidden"
          data-testid="shooter-upload-file"
        />
        <div className="flex flex-col sm:flex-row items-center gap-3">
          <button
            onClick={() => fileRef.current?.click()}
            disabled={busy || !title.trim()}
            className="btn-primary inline-flex items-center gap-2 disabled:opacity-40"
            data-testid="shooter-upload-btn"
          >
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
            {busy ? `Uploading… ${progress}%` : `Pick ${isPhoto ? "photo" : "video"} to upload`}
          </button>
          {busy && (
            <div className="flex-1 w-full bg-stone-900 h-1.5">
              <div className="bg-sky-500 h-full transition-all" style={{ width: `${progress}%` }} />
            </div>
          )}
        </div>
      </div>

      {/* Item list */}
      <div className="flex items-center gap-2 mb-3">
        <Film size={13} className="text-stone-400" />
        <h2 className="font-display uppercase text-2xl font-bold tracking-tight">
          Your Listings <span className="text-stone-500 text-lg font-normal">({items.filter((it) => it.kind === kind).length})</span>
        </h2>
      </div>
      {items.filter((it) => it.kind === kind).length === 0 ? (
        <div className="border border-dashed border-stone-800 p-12 text-center text-stone-500 text-sm" data-testid="shooter-empty">
          No listings yet. Upload one above to get started.
        </div>
      ) : (
        <div className="flex flex-col lg:flex-row gap-6" data-testid="shooter-listings">
          <FolderSidebar
            scope={scope}
            active={activeFolder}
            onChange={setActiveFolder}
            folders={folderState.folders}
            unfiledCount={folderState.unfiledCount}
            total={folderState.total || items.filter((it) => it.kind === kind).length}
            onReload={folderState.reload}
          />
          <div className="flex-1 min-w-0">
            <div className="relative mb-3">
              <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-500" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={`Search ${isPhoto ? "photos" : "videos"}…`}
                className="w-full bg-stone-950 border border-stone-800 focus:border-sky-600 pl-9 pr-8 py-2 text-sm text-stone-100 placeholder-stone-500 focus:outline-none"
                data-testid="shooter-search"
              />
              {search && (
                <button
                  onClick={() => setSearch("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-stone-500 hover:text-stone-100"
                  title="Clear"
                >
                  <X size={12} />
                </button>
              )}
            </div>
            {visibleItems.length === 0 ? (
              <div className="border border-dashed border-stone-800 p-10 text-center text-stone-500 text-sm">
                Nothing here yet.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="shooter-items-grid">
                {visibleItems.map((it) => (
                  <ItemCard
                    key={it.id}
                    item={it}
                    wmText={user?.watermark_text || ""}
                    scope={scope}
                    folders={folderState.folders}
                    onRename={() => renameItem(it)}
                    onReprice={() => repriceItem(it)}
                    onToggle={() => toggleActive(it)}
                    onDelete={() => deleteItem(it)}
                    onMoved={reloadItemsAndFolders}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </PortalShell>
  );
}

function ItemCard({ item, wmText, scope, folders, onRename, onReprice, onToggle, onDelete, onMoved }) {
  const isPhoto = item.kind === "photo";
  const src = `${BACKEND_URL}/api/marketplace/items/${item.id}/preview`;
  return (
    <div className={`border ${item.active ? "border-stone-800" : "border-stone-800 opacity-50"} hover:border-sky-700 transition-colors`} data-testid={`shooter-item-${item.id}`}>
      <div className="aspect-video bg-black relative overflow-hidden">
        {isPhoto ? (
          <img src={src} alt={item.title} className="w-full h-full object-cover" />
        ) : (
          <video
            src={src}
            controls
            controlsList="nodownload"
            playsInline
            preload="metadata"
            className="w-full h-full object-contain bg-black"
            data-testid={`shooter-item-video-${item.id}`}
          />
        )}
        {/* Watermark overlay — visible during preview only, buyers get clean files */}
        {wmText && (
          <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
            <div className="font-display text-white/70 text-3xl uppercase tracking-widest rotate-[-25deg] mix-blend-difference select-none">
              {wmText}
            </div>
          </div>
        )}
        <div className="absolute top-1.5 left-1.5 bg-black/70 px-2 py-0.5 text-[10px] uppercase tracking-widest">
          {item.kind}
        </div>
        {item.duration > 0 && (
          <div className="absolute bottom-1.5 right-1.5 font-mono-tc text-[10px] bg-black/70 px-1.5 py-0.5">
            {fmtDuration(item.duration)}
          </div>
        )}
      </div>
      <div className="p-3">
        <div className="flex items-start gap-2 mb-2">
          <div className="flex-1 min-w-0">
            <div className="font-display uppercase font-bold truncate">{item.title || "Untitled"}</div>
            {item.description && (
              <div className="text-[11px] text-stone-500 truncate">{item.description}</div>
            )}
          </div>
          <button onClick={onRename} className="text-stone-500 hover:text-sky-400" title="Rename" data-testid={`shooter-item-rename-${item.id}`}>
            <Pencil size={13} />
          </button>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onReprice}
            className="btn-secondary !py-1 !px-2 text-[11px] inline-flex items-center gap-1"
            data-testid={`shooter-item-price-${item.id}`}
          >
            <DollarSign size={11} /> {dollars(item.price_cents || 0)}
          </button>
          <button
            onClick={onToggle}
            className={`btn-secondary !py-1 !px-2 text-[11px] inline-flex items-center gap-1 ${item.active ? "!text-emerald-400" : "!text-stone-500"}`}
            title={item.active ? "Listed — click to hide" : "Hidden — click to list"}
            data-testid={`shooter-item-toggle-${item.id}`}
          >
            {item.active ? <Eye size={11} /> : <EyeOff size={11} />}
            {item.active ? "Live" : "Hidden"}
          </button>
          <MoveToFolderMenu
            scope={scope}
            itemId={item.id}
            currentFolderId={item.folder_id}
            folders={folders || []}
            onMoved={onMoved}
            className="ml-auto"
          />
          <button
            onClick={onDelete}
            className="text-stone-500 hover:text-rose-400"
            title="Delete"
            data-testid={`shooter-item-del-${item.id}`}
          >
            <Trash2 size={13} />
          </button>
        </div>
      </div>
    </div>
  );
}
